import Icon from '@mdi/react';
import { mdiDownload, mdiFileDocumentOutline } from '@mdi/js';
import React, { useCallback, useMemo, useState } from 'react';
import { BaseDialog, Variables } from '../../base';
import { buildStatementPrintHtml, downloadStatementPdf } from './statement-print-download';
import type { StatementPrintViewModel } from './statement-print.types';
import * as S from './statement-print-preview.styles';

type StatementPrintPreviewBaseProps = {
    open?: boolean;
    onClose: () => void;
    onError?: (message: string) => void;
    title?: string;
};

type StatementPrintPreviewFromModel = StatementPrintPreviewBaseProps & {
    model: StatementPrintViewModel;
    html?: undefined;
    onDownload?: undefined;
};

type StatementPrintPreviewFromHtml = StatementPrintPreviewBaseProps & {
    html: string;
    onDownload: () => Promise<void>;
    model?: undefined;
};

export type StatementPrintPreviewProps = StatementPrintPreviewFromModel | StatementPrintPreviewFromHtml;

export const StatementPrintPreview: React.FC<StatementPrintPreviewProps> = props => {
    const { open = true, onClose, onError, title = 'PDF preview — Patient Statement' } = props;
    const model = 'model' in props ? props.model : undefined;
    const htmlProp = 'html' in props ? props.html : undefined;
    const onDownload = 'onDownload' in props ? props.onDownload : undefined;
    const [frameHeight, setFrameHeight] = useState(1100);
    const [isDownloading, setIsDownloading] = useState(false);

    const html = useMemo(() => {
        if (htmlProp) {
            return htmlProp;
        }
        if (model) {
            return buildStatementPrintHtml(model);
        }
        return '';
    }, [htmlProp, model]);

    const handleFrameLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>): void => {
        const doc = event.currentTarget.contentDocument;
        const height = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight;
        if (height && height > 400) {
            setFrameHeight(height + 24);
        }
    }, []);

    const handleDownload = async (): Promise<void> => {
        setIsDownloading(true);
        try {
            if (onDownload) {
                await onDownload();
            } else if (model) {
                await downloadStatementPdf(model);
            }
        } catch (err) {
            console.error(err);
            onError?.('Unable to download PDF. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <BaseDialog
            open={open}
            fullScreen={true}
            expandable={true}
            onClose={onClose}
            aria-label={title}
            slotProps={{
                paper: {
                    style: {
                        background: Variables.Colors.n15,
                    },
                },
            }}>
            <S.DialogBody>
                <S.Toolbar>
                    <Icon path={mdiFileDocumentOutline} size={0.85} color={Variables.Swatches.White} />
                    <S.ToolbarTitle>{title}</S.ToolbarTitle>
                    <S.ToolbarActions>
                        <S.ToolbarGhostButton type="button" variant="unstyled" onClick={onClose}>
                            Close
                        </S.ToolbarGhostButton>
                        <S.ToolbarPrimaryButton
                            type="button"
                            variant="unstyled"
                            onClick={handleDownload}
                            disabled={isDownloading}>
                            <Icon path={mdiDownload} size={0.75} color={Variables.Swatches.White} />
                            {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                        </S.ToolbarPrimaryButton>
                    </S.ToolbarActions>
                </S.Toolbar>
                <S.PaperWrap>
                    <S.PaperShadow>
                        <S.PreviewFrame
                            title="Patient statement preview"
                            srcDoc={html}
                            style={{ height: frameHeight }}
                            onLoad={handleFrameLoad}
                        />
                    </S.PaperShadow>
                </S.PaperWrap>
            </S.DialogBody>
        </BaseDialog>
    );
};
